import type {
  DiscoveryInfluencer,
  PlatformsData,
  PlatformObject,
} from "../types/discovery.types";
import type {
  DiscoveryInfluencerProfile,
  PlatformsData as ProfilePlatformsData,
} from "../types/discoveryProfile.types";
import type { DiscoverySearchRpcRow } from "../ports/discoverySearch.port";
import type { DiscoveryProfileRpcData } from "../ports/discoveryProfile.port";

export const MIN_TOKEN_LENGTH = 3;

export function formatFollowers(count: number | null | undefined): string {
  if (!count) return "0";
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

export function capitalizeTier(tier: string | null | undefined): string {
  if (!tier) return "Nano";
  return tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
}

export function formatEngagement(rate: number | null | undefined): string {
  if (!rate) return "0%";
  return `${rate.toFixed(1)}%`;
}

export function formatNumber(num: number | null | undefined): string {
  if (!num) return "0";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function getEstimatedRateCard(userCountry?: string | null): string {
  if (
    userCountry &&
    (userCountry.toLowerCase() === "india" || userCountry.toLowerCase() === "in")
  ) {
    return "₹15K-30K";
  }
  return "$150-300";
}

export function validateSearchTokens(searchTerm?: string | null): {
  effectiveSearchTerm: string | null;
  clientWarnings: string[];
} {
  const clientWarnings: string[] = [];
  let effectiveSearchTerm = searchTerm?.trim() || null;

  if (effectiveSearchTerm) {
    const tokens = effectiveSearchTerm.split(/\s+/).filter((t) => t.length > 0);
    const shortTokens = tokens.filter((t) => t.length < MIN_TOKEN_LENGTH);
    const validTokens = tokens.filter((t) => t.length >= MIN_TOKEN_LENGTH);

    if (shortTokens.length > 0 && validTokens.length === 0) {
      clientWarnings.push(`Search requires at least ${MIN_TOKEN_LENGTH} characters`);
      effectiveSearchTerm = null;
    } else if (shortTokens.length > 0) {
      clientWarnings.push(
        `Terms "${shortTokens.join('", "')}" will be ignored (min ${MIN_TOKEN_LENGTH} chars)`
      );
    }
  }

  return { effectiveSearchTerm, clientWarnings };
}

export function transformSearchRow(
  inf: DiscoverySearchRpcRow,
  userCountry?: string | null
): DiscoveryInfluencer {
  const platformsRaw = inf.platforms as unknown as PlatformsData | null;
  const platformsData: Array<{ platform_id: string } & PlatformObject> = platformsRaw
    ? Object.entries(platformsRaw).map(([platformId, data]) => ({
        platform_id: platformId,
        ...data,
      }))
    : [];

  const platformsList = platformsData.map((p) => p.platform_id);
  const followersMap: Record<string, string> = {};

  let totalAvgLikes = 0;
  let totalAvgComments = 0;
  let totalAvgViews = 0;
  let totalEstimatedReach = 0;

  platformsData.forEach((p) => {
    if (p.followers_count) {
      followersMap[p.platform_id] = formatFollowers(p.followers_count);
    }
    if (p.metrics) {
      totalAvgLikes += p.metrics.avg_likes || 0;
      totalAvgComments += p.metrics.avg_comments || 0;
      totalAvgViews += p.metrics.avg_views || 0;
      totalEstimatedReach += p.metrics.estimated_reach || 0;
    }
  });

  return {
    id: inf.id,
    name: inf.name,
    verified: inf.is_verified || false,
    handle: inf.primary_handle.startsWith("@")
      ? inf.primary_handle
      : `@${inf.primary_handle}`,
    location:
      inf.city && inf.country
        ? `${inf.city}, ${inf.country}`
        : inf.country || "India",
    rating: 4.5,
    platforms: platformsList,
    followers: followersMap,
    engagement: formatEngagement(inf.avg_engagement_rate),
    rate: getEstimatedRateCard(userCountry),
    tier: capitalizeTier(inf.tier),
    match: "95%",
    languages: (inf.languages || []).map(
      (l: string) => l.charAt(0).toUpperCase() + l.slice(1)
    ),
    categories: (inf.categories || []).map(
      (c: string) => c.charAt(0).toUpperCase() + c.slice(1)
    ),
    recentPosts: [null, null, null, null],
    bio: inf.bio || "Bio not available",
    profilePic: inf.profile_pic_path || null,
    avgLikes: totalAvgLikes > 0 ? formatNumber(totalAvgLikes) : null,
    avgComments: totalAvgComments > 0 ? formatNumber(totalAvgComments) : null,
    avgViews: totalAvgViews > 0 ? formatNumber(totalAvgViews * 2.65) : null,
    estimatedReach:
      totalEstimatedReach > 0 ? formatNumber(totalEstimatedReach) : null,
    qualityScore: inf.quality_score || null,
    relevanceScore: inf.relevance_score || 0,
    locationScore: inf.location_score || 0,
    rawFollowersCount: inf.total_followers_count || 0,
    rawEngagementRate: inf.avg_engagement_rate || 0,
    rawCity: inf.city || null,
    rawCountry: inf.country || null,
    rawPlatforms: platformsRaw || {},
    rawCategories: inf.categories || [],
  };
}

export function transformProfileData(
  data: DiscoveryProfileRpcData
): DiscoveryInfluencerProfile {
  return {
    id: data.id,
    name: data.name,
    handle: data.handle,
    bio: data.bio,
    profilePic: data.profile_pic_path,
    gender: data.gender,
    location: data.location,
    tier: data.tier,
    verified: data.is_verified || false,

    totalFollowers: data.total_followers_count || 0,
    totalFollowing: data.total_following_count || 0,
    totalPosts: data.total_posts || 0,

    platforms: data.platform_list || [],
    instagramFollowers: data.instagram_followers ?? null,
    youtubeFollowers: data.youtube_followers ?? null,

    avgLikes: Number(data.avg_likes) || 0,
    avgComments: Number(data.avg_comments) || 0,
    avgViews: (Number(data.avg_views) || 0) * 2.65,
    totalEngagement: Number(data.total_engagement) || 0,
    engagementRate: data.avg_engagement_rate || 0,
    estimatedReach: Number(data.estimated_reach) || 0,
    qualityScore: data.quality_score || 0,
    dataQualityScore: data.data_quality_score || 0,

    categories: data.categories || [],
    languages: data.languages || [],

    hasEmail: data.has_email || false,

    rawPlatforms: (data.platforms as ProfilePlatformsData) || {},

    pastCollaboratedBrands: Array.isArray(data.past_collaborated_brands)
      ? (data.past_collaborated_brands as string[]).map((brandName: string) => ({
          brand_name: brandName,
        }))
      : [],
    pastCollaboratedBrandsCount: data.past_collaborated_brands_count || 0,
    lastBrandCollaborationAt: data.last_brand_collaboration_at || null,
  };
}
